import { useState } from 'react';
import { Filter, SearchInput, SelectInput, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Avatar, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { CheckCircle } from '@mui/icons-material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { ListPageHeader } from '@/components/ListPageHeader';
import { DEEMING_ALERT_STATUS, ALERT_URGENCY_COLORS } from './constants';
import { DeemingCountdownBar } from './DeemingCountdownBar';

interface DeemingAlertItem {
  id: string;
  alertStatus: string;
  employee?: { id: string };
  vendorName?: string;
  tenureDays?: number;
  assignedTo?: string;
  startDate?: string;
}

export const DeemingAlertList = () => {
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: alerts, isLoading } = useGetList('deeming-alerts', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const sortedAlerts = [...(alerts ?? [])].sort((a: DeemingAlertItem, b: DeemingAlertItem) => {
    const urgencyOrder: Record<string, number> = { Escalated: 0, 'Pending Review': 1, 'Under Investigation': 2, Resolved: 3 };
    return (urgencyOrder[a.alertStatus] ?? 4) - (urgencyOrder[b.alertStatus] ?? 4);
  });

  const stats = {
    total: sortedAlerts.length,
    escalated: sortedAlerts.filter((a: DeemingAlertItem) => a.alertStatus === 'Escalated').length,
    pendingReview: sortedAlerts.filter((a: DeemingAlertItem) => a.alertStatus === 'Pending Review').length,
    investigating: sortedAlerts.filter((a: DeemingAlertItem) => a.alertStatus === 'Under Investigation').length,
    resolved: sortedAlerts.filter((a: DeemingAlertItem) => a.alertStatus === 'Resolved').length,
  };

  const statusCounts: Record<string, number> = sortedAlerts.reduce((acc: Record<string, number>, a: DeemingAlertItem) => {
    acc[a.alertStatus] = (acc[a.alertStatus] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(statusCounts).map(([label, value]) => ({ label, value }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Deeming Alerts"
          metrics={[
            { label: 'Escalated', value: String(stats.escalated), color: 'error.main' },
            { label: 'Pending', value: String(stats.pendingReview), color: 'warning.main' },
            { label: 'Investigating', value: String(stats.investigating), color: 'info.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {sortedAlerts.map((alert: DeemingAlertItem) => (
            <Paper key={alert.id} sx={{ p: 2 }} onClick={() => navigate(`/deeming-alerts/${alert.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: ALERT_URGENCY_COLORS[alert.alertStatus] === 'error' ? 'error.main' : ALERT_URGENCY_COLORS[alert.alertStatus] === 'warning' ? 'warning.main' : 'info.main', fontSize: 14, color: 'white' }}>
                    {getEmployeeName(employeeMap, alert.employee?.id)?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, alert.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{alert.vendorName}</Typography>
                  </Box>
                </Box>
                <Chip label={alert.alertStatus} size="small" color={ALERT_URGENCY_COLORS[alert.alertStatus] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Tenure: {alert.tenureDays} days</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">Assigned: {alert.assignedTo}</Typography>
              </Box>
                  <DeemingCountdownBar startDate={alert.startDate ?? ''} thresholdDays={90} />
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Deeming Alerts"
        metrics={[
          { label: 'Escalated', value: String(stats.escalated), color: 'error.main' },
          { label: 'Pending Review', value: String(stats.pendingReview), color: 'warning.main' },
          { label: 'Investigating', value: String(stats.investigating), color: 'info.main' },
          { label: 'Resolved', value: String(stats.resolved), color: 'success.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Tenure (days)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Alert Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {sortedAlerts.map((alert: DeemingAlertItem) => (
              <TableRow
                key={alert.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/deeming-alerts/${alert.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: ALERT_URGENCY_COLORS[alert.alertStatus] === 'error' ? 'error.main' : ALERT_URGENCY_COLORS[alert.alertStatus] === 'warning' ? 'warning.main' : 'info.main', fontSize: 12, color: 'white' }}>
                      {getEmployeeName(employeeMap, alert.employee?.id)?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, alert.employee?.id)}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{alert.vendorName}</Typography>
                </TableCell>
                <TableCell>
              <DeemingCountdownBar startDate={alert.startDate ?? ''} thresholdDays={90} />
                </TableCell>
                <TableCell>
                  <Chip label={alert.alertStatus} size="small" color={ALERT_URGENCY_COLORS[alert.alertStatus] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{alert.assignedTo}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sortedAlerts.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!sortedAlerts || sortedAlerts.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography color="text.secondary">No deeming alerts</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const DeemingAlertFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search employee or vendor" />
    <SelectInput source="alertStatus" choices={DEEMING_ALERT_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);
